"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const products = [{
  id: 1,
  title: "MINI EXCAVATOR | 3.5 TON",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/excavator_3_5_ton.png",
  alt: "3.5 Ton mini excavator on construction site",
  link: "/EquipmentPage"
}, {
  id: 2,
  title: "COMPACT BACKHOE | 4X4 LOADER",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/backhoe_loader_4x4_precision.png",
  alt: "Heavy duty 4x4 backhoe loader on construction site",
  link: "/EquipmentPage"
}, {
  id: 3,
  title: "SKID STEER LOADER | 2 SPEED S76",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/skid_steer_bobcat_s76.png",
  alt: "White Bobcat S76 skid steer loader on industrial site",
  link: "/EquipmentPage"
}, {
  id: 4,
  title: "TRACK LOADER | COMPACT SVL75",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/track_loader_kubota_svl75.png",
  alt: "Orange Kubota compact track loader side view",
  link: "/EquipmentPage"
}, {
  id: 5,
  title: "COMPACT LOADER | WHEEL LOADER 221F",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/wheel_loader_case_221f.png",
  alt: "Tan Case compact wheel loader bucket down",
  link: "/EquipmentPage"
}, {
  id: 6,
  title: "TELEHANDLER | TELESCOPIC G5",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/telehandler_jlg_g5.png",
  alt: "Orange JLG telehandler with raised forks",
  link: "/EquipmentPage"
}, {
  id: 7,
  title: "TRENCH ROLLER | WACKER NEUSON RTX",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/roller_wacker_neuson_rtx.png",
  alt: "Yellow Wacker Neuson trench roller on dirt",
  link: "/EquipmentPage"
}, {
  id: 8,
  title: "DUMP TRUCK | ARTICULATED 707G",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/dump_truck_hydrema_707g.png",
  alt: "Blue Hydrema articulated compact dump truck",
  link: "/EquipmentPage"
}];
export default function Products() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(3);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const updateItemsToShow = useCallback(() => {
    setItemsToShow(3);
  }, []);
  useEffect(() => {
    updateItemsToShow();
  }, [updateItemsToShow]);
  const maxIndex = Math.max(0, products.length - itemsToShow);
  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1);
  }, [maxIndex]);
  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => prev <= 0 ? maxIndex : prev - 1);
  }, [maxIndex]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
    touchStartX.current = null;
    touchEndX.current = null;
  };
  return <section data-section-id="118174" id="our-rental-fleet" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-32 md:mb-40">
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-foreground mb-4 uppercase font-sans">
            Our Rental Fleet
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-sans">
            Our Complete Fleet & Full Equipment Lineup engineered for reliability and efficiency on every professional job site.
          </p>
        </div>

        <div className="relative group/carousel">
          <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
            <div className="flex transition-transform duration-500 ease-out" style={{
            transform: `translateX(-${currentIndex * (100 / itemsToShow)}%)`
          }}>
              {products.map((product, i) => <div key={product.id} data-index={i} className="relative pt-12 h-full shrink-0 px-4" style={{
              width: `${100 / itemsToShow}%`
            }}>
                  <Card className="group h-full pt-24 pb-8 px-4 flex flex-col items-center text-center border-border/40 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2.5 bg-card relative overflow-visible rounded-sm">
                    <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[70%] h-40 z-10 transition-transform duration-500 group-hover:scale-105 pointer-events-none">
                      <img src={product.image} data-wvc-srcset={`${product.image}-wvc-srcset`} data-wvc-sizes={`${product.image}-wvc-sizes`} alt={product.alt} loading="lazy" className="w-full h-full object-contain drop-shadow-[0_20px_20px_rgba(0,0,0,0.15)]" />
                    </div>

                    <Separator className="w-12 h-[3px] bg-primary mb-6 shrink-0" />
                    
                    <CardTitle className="text-lg font-bold mb-4 tracking-tighter text-card-foreground font-mono uppercase">
                      {product.title}
                    </CardTitle>
                  </Card>
                </div>)}
            </div>
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 -left-4 md:-left-6 z-20">
            <Button variant="outline" size="icon" onClick={prevSlide} className="rounded-full bg-background/80 backdrop-blur-sm border-border/50 shadow-lg hover:bg-primary hover:text-primary-foreground transition-colors" aria-label="Previous slide">
              <ChevronLeft className="size-6" />
            </Button>
          </div>
          <div className="absolute top-1/2 -translate-y-1/2 -right-4 md:-right-6 z-20">
            <Button variant="outline" size="icon" onClick={nextSlide} className="rounded-full bg-background/80 backdrop-blur-sm border-border/50 shadow-lg hover:bg-primary hover:text-primary-foreground transition-colors" aria-label="Next slide">
              <ChevronRight className="size-6" />
            </Button>
          </div>

          <div className="flex justify-center gap-2 mt-12">
            {Array.from({
            length: maxIndex + 1
          }).map((_, idx) => <Button key={idx} variant="ghost" onClick={() => setCurrentIndex(idx)} className={`p-0 min-w-0 h-1.5 transition-all duration-300 rounded-full hover:bg-transparent ${currentIndex === idx ? "w-8 bg-primary" : "w-2 bg-muted-foreground/30"}`} aria-label={`Go to slide ${idx + 1}`} />)}
          </div>
        </div>
      </div>
    </section>;
}